/* eslint-disable react-hooks/rules-of-hooks */
import { useCallback, useEffect, useMemo, useState } from 'react';
import useOfferFilterLoanParams from '../../hooks/useOfferFilterLoanParams';
import {
  GetPdpModalConfigAction,
  RenderPdpModalHero,
} from '../../types/cmsTypes';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../../utils/analytics';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import {
  getQuickFacts,
  getTIUniqueSellingPoint,
  groupProductAttributes,
  PersonalLoanFilterConfig,
} from '../../utils/helpers';
import { getChooseOneOffers } from '../../utils/helpers/offers';
import { LoanSearchParams } from '../../utils/searchParams/loanSearchParams';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import { CarInsuranceCtaBuilderInstance } from '../CarInsurance/CarInsuranceCtaBuilder';
import { TravelInsuranceCtaType } from '../TravelInsuranceCtaBuilder';
import { allowedTIVerticals, coverageAttributesList } from './constants';
import { TranslationKey, TRANSLATIONS } from './i18n';
import { PromoDetailsCI } from './PromoDetailsCI';
import { SectionCoverage } from './SectionCoverage';
import SectionDetails from './SectionDetails';
import SectionGifts, { SectionGiftsProps } from './SectionGifts';
import { SectionPremiumCI } from './SectionPremiumCI';
import { SectionStepsToApply } from './SectionStepsToApply';
import SectionTake from './SectionTake';
import { ProductCardProps, SectionConfig, SectionType } from './types';
import {
  buildSectionExclusiveOfferData,
  buildSectionWelcomeOfferData,
  getIconSectionDetail,
  getTitleSectionDetail,
} from './utils';

type ProductCardSectionsMapperProps = {
  productCardProps: ProductCardProps;
  loanParams: LoanSearchParams | undefined;
  plFilterConfig: PersonalLoanFilterConfig | undefined;
  carInsuranceCtaComponent: CarInsuranceCtaBuilderInstance | null;
  priceLine: React.ReactNode | null;
  productDetailsModalKeys?: string[];
  /** For the marker-path ProductDetails modal; travels as a prop (CMS-1924). */
  getPdpModalConfigAction?: GetPdpModalConfigAction;
  isPreview?: boolean;
  renderPdpModalHero?: RenderPdpModalHero;
  travelInsuranceCtaComponent?: (
    travelInsuranceCtaType: TravelInsuranceCtaType,
  ) => JSX.Element | undefined;
};

export const ProductCardSectionsMapper: React.FC<
  ProductCardSectionsMapperProps
> = (props) => {
  const {
    loanParams,
    productCardProps,
    carInsuranceCtaComponent,
    priceLine,
    plFilterConfig,
    productDetailsModalKeys,
    getPdpModalConfigAction,
    isPreview,
    renderPdpModalHero,
    travelInsuranceCtaComponent,
  } = props;
  const context = useCmsClientContext();
  const { lang, brand, currencyDisplay: prefix } = context;

  const staticText = getClientTranslations(TRANSLATIONS, context);
  const {
    index,
    url,
    vertical,
    carInsuranceQuote,
    carInsuranceSection,
    product,
    pushDataLayer,
    approvalOdds,
    approvalOddsState,
  } = productCardProps;
  const {
    attributes: productAttributes,
    exclusive_offers,
    bundle_exclusive_offers,
    long_description,
    welcome_offers,
  } = product;

  if (!vertical) {
    return;
  }

  const [sectionVisibility, setSectionVisibility] = useState<{
    [key in SectionType]: boolean;
  }>({
    detail: false,
    rewardExclusive: false,
    rewardWelcome: false,
    pros: false,
    promoCI: false,
    coverage: false,
    premium: false,
    stepsToApply: false,
  });

  // Memoize the merged array so its reference stays stable across renders;
  // otherwise the new literal would bust the filtering memo in
  // useOfferFilterLoanParams (and downstream useMemos) on every render.
  const exclusiveAndBundleOffers = useMemo(
    () => getChooseOneOffers({ exclusive_offers, bundle_exclusive_offers }),
    [exclusive_offers, bundle_exclusive_offers],
  );

  const { productExclusiveOffer, productWelcomeOffer } =
    useOfferFilterLoanParams({
      welcomeOffer: welcome_offers ?? [],
      exclusiveOffer: exclusiveAndBundleOffers,
      loanParams,
      plFilterConfig: props.plFilterConfig,
    });

  const tiUniqueSellingPoint = useMemo(
    () => getTIUniqueSellingPoint(product.attributes)?.uniqueSellingPoint,
    [product.attributes],
  );

  const titleSectionDetail = getTitleSectionDetail(vertical);
  const iconSectionDetail = getIconSectionDetail(vertical);

  const productAttributesGroup = useMemo(
    () => groupProductAttributes(product),
    [product],
  );
  const quickFacts = useMemo(
    () => getQuickFacts(productAttributes)?.quickFacts,
    [productAttributes],
  );
  const thingsConsider = useMemo(
    () => getQuickFacts(productAttributes)?.thingsConsider,
    [productAttributes],
  );

  const stepsToApply = useMemo(
    () =>
      productAttributes
        ?.filter(
          (a) => a.__typename === 'ComponentAttributesAttrRichTextEditor',
        )
        .find((a) => a.name?.name === 'STEPS_TO_APPLY')?.value,
    [productAttributes],
  );

  const sendAnalyticsForSection = useCallback(
    (section: SectionType, titleSection: string, alreadyOpened: boolean) => {
      const componentTitle = {
        rewardWelcome: `Product Card/Rewards Accordion_${titleSection}`,
        rewardExclusive: `Product Card/Rewards Accordion_${titleSection}`,
        pros: `Product Card/Pros&Cons_${titleSection}`,
        detail: `Product Card/Product Details_${titleSection}`,
        promoCI: `Product Card/Product promo CI_${titleSection}`,
        coverage: `Product Card/Product coverage CI_${titleSection}`,
        premium: `Product Card/Product premium CI_${titleSection}`,
        stepsToApply: `Product Card/Product stepsToApply CI_${titleSection}`,
      };

      sendProductAnalytics({
        action: alreadyOpened ? UserActionEnum.COLLAPSE : UserActionEnum.EXPAND,
        componentTitle: componentTitle[section],
        componentType: ComponentTypeEnum.ACCORDION,
        event: 'product_interaction',
        index: index,
        url: url,
        product: product,
        pushDataLayer,
        approvalDegree: approvalOdds,
        approvalDegreeState: approvalOddsState,
      });
    },
    [index, url, product, pushDataLayer, approvalOdds, approvalOddsState],
  );

  const toggleSectionVisible = useCallback(
    (section: SectionType, titleSection?: string) => {
      const sectionVisibilityCopy = { ...sectionVisibility };

      const alreadyOpened = sectionVisibilityCopy[section];

      Object.keys(sectionVisibilityCopy).forEach((key) => {
        sectionVisibilityCopy[key as SectionType] = false;
      });

      if (!alreadyOpened) {
        sectionVisibilityCopy[section] = true;
      }

      setSectionVisibility(sectionVisibilityCopy);

      if (titleSection) {
        sendAnalyticsForSection(section, titleSection, alreadyOpened);
      }
    },
    [sectionVisibility, setSectionVisibility, sendAnalyticsForSection],
  );

  const buildSectionExclusiveOffer = useMemo(
    () =>
      buildSectionExclusiveOfferData({
        productCardProps,
        context,
        prefix,
        vertical,
        toggleSectionVisible,
        loanSearchParams: loanParams,
        isVisible: sectionVisibility.rewardExclusive,
        offersWithFilteredGifts: productExclusiveOffer ?? [],
        carInsuranceCtaComponent,
        priceLine,
        plFilterConfig,
        productDetailsModalKeys,
        travelInsuranceCtaComponent,
      }),
    [
      productCardProps,
      context,
      prefix,
      vertical,
      toggleSectionVisible,
      loanParams,
      sectionVisibility.rewardExclusive,
      productExclusiveOffer,
      carInsuranceCtaComponent,
      priceLine,
      plFilterConfig,
      productDetailsModalKeys,
      travelInsuranceCtaComponent,
    ],
  );
  const dataGiftsExclusive: SectionGiftsProps = buildSectionExclusiveOffer.data;

  const buildSectionWelcomeOffer = useMemo(
    () =>
      buildSectionWelcomeOfferData({
        productCardProps,
        context,
        prefix,
        vertical,
        toggleSectionVisible,
        loanSearchParams: loanParams,
        isVisible: sectionVisibility.rewardWelcome,
        offersWithFilteredGifts: productWelcomeOffer ?? [],
        carInsuranceCtaComponent,
        priceLine,
        plFilterConfig,
        productDetailsModalKeys,
        travelInsuranceCtaComponent,
      }),
    [
      productCardProps,
      context,
      prefix,
      vertical,
      toggleSectionVisible,
      loanParams,
      sectionVisibility.rewardWelcome,
      productWelcomeOffer,
      carInsuranceCtaComponent,
      priceLine,
      plFilterConfig,
      productDetailsModalKeys,
      travelInsuranceCtaComponent,
    ],
  );

  const dataGiftsWelcome = buildSectionWelcomeOffer.data;

  const dataTake = {
    isVisible: sectionVisibility.pros,
    productAttributes: productAttributes ?? [],
    toggleVisibility: () => toggleSectionVisible('pros', staticText.brand_take),
    vertical,
    productCardProps,
  };

  const dataDetails = {
    isVisible: sectionVisibility.detail,
    vertical: vertical,
    long_description: long_description,
    product: productCardProps.product,
    title: titleSectionDetail,
    icon: iconSectionDetail,
    toggleVisibility: () =>
      toggleSectionVisible('detail', tr(titleSectionDetail, staticText)),
  };

  const dataPromoDetailsCI = {
    translationContext: {
      brand,
      lang,
    },
    isVisible: sectionVisibility.promoCI,
    title: staticText.drive_protected,
    icon: iconSectionDetail,
    content: carInsuranceSection?.promoDetails ?? '',
    toggleVisibility: () =>
      toggleSectionVisible('promoCI', tr('drive_protected', staticText)),
  };

  const dataCoverageCI = {
    isVisible: sectionVisibility.coverage,
    toggleVisibility: () =>
      toggleSectionVisible('coverage', tr('coverage', staticText)),
    productAttributes: productAttributes ?? [],
  };

  const dataPremiumCI = {
    isVisible: sectionVisibility.premium,
    toggleVisibility: () =>
      toggleSectionVisible('premium', tr('premium', staticText)),
    productName: product.product_name ?? '',
    carInsuranceQuote: carInsuranceQuote,
    prefix: prefix ?? '',
  };

  const dataStepsToApply = {
    isVisible: sectionVisibility.stepsToApply,
    toggleVisibility: () =>
      toggleSectionVisible('stepsToApply', tr('steps_to_apply', staticText)),
    content: stepsToApply ?? '',
    title: 'steps_to_apply' as TranslationKey,
    icon: 'steppers' as const,
  };

  const sections: Array<SectionConfig> = [
    {
      id: 'rewardExclusive',
      shouldShow:
        buildSectionExclusiveOffer.shouldRenderSection ||
        buildSectionExclusiveOffer.shouldShowEligibilityHint,
      render: () => (
        <SectionGifts
          {...dataGiftsExclusive}
          getPdpModalConfigAction={getPdpModalConfigAction}
          isPreview={isPreview}
          renderPdpModalHero={renderPdpModalHero}
        />
      ),
    },
    {
      id: 'rewardWelcome',
      shouldShow:
        buildSectionWelcomeOffer.shouldRenderSection ||
        buildSectionWelcomeOffer.shouldShowEligibilityHint,
      render: () => (
        <SectionGifts
          {...dataGiftsWelcome}
          getPdpModalConfigAction={getPdpModalConfigAction}
          isPreview={isPreview}
          renderPdpModalHero={renderPdpModalHero}
        />
      ),
    },
    {
      id: 'stepsToApply',
      shouldShow:
        !!stepsToApply &&
        !!vertical &&
        allowedTIVerticals.includes(vertical) &&
        !!dataStepsToApply.content,
      render: () => <SectionStepsToApply {...dataStepsToApply} />,
    },
    {
      id: 'pros',
      shouldShow:
        (vertical !== 'TRAVEL_INSURANCE' &&
          (quickFacts?.pros?.length > 0 ||
            quickFacts?.cons?.length > 0 ||
            thingsConsider?.pros?.length > 0 ||
            thingsConsider?.cons?.length > 0)) ||
        (vertical === 'TRAVEL_INSURANCE' &&
          !!tiUniqueSellingPoint?.pros &&
          tiUniqueSellingPoint?.pros?.length > 0),
      render: () => <SectionTake {...dataTake} />,
    },
    {
      id: 'detail',
      shouldShow:
        !!long_description ||
        !!productAttributesGroup.eligibility.length ||
        !!productAttributesGroup.fees.length ||
        !!productAttributesGroup.repayment.length,
      render: () => <SectionDetails {...dataDetails} />,
    },
    {
      id: 'promoCI',
      shouldShow: !!carInsuranceSection?.promoDetails,
      render: () => <PromoDetailsCI {...dataPromoDetailsCI} />,
    },
    {
      id: 'coverage',
      shouldShow: !!productAttributes?.some((a) =>
        coverageAttributesList.includes(a.name?.name ?? ''),
      ),
      render: () => <SectionCoverage {...dataCoverageCI} />,
    },
    {
      id: 'premium',
      shouldShow:
        Object.keys(carInsuranceQuote?.prices ?? {}).length > 0 ||
        Object.keys(carInsuranceQuote?.taxes ?? {}).length > 0,
      render: () => <SectionPremiumCI {...dataPremiumCI} />,
    },
  ];

  const defaultOpenSection: SectionType | null =
    vertical === 'TRAVEL_INSURANCE'
      ? null
      : (sections.find((s) => s.shouldShow)?.id ?? 'detail');
  useEffect(() => {
    toggleSectionVisible(defaultOpenSection ?? 'detail');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOpenSection]);

  return (
    // eslint-disable-next-line tailwindcss/no-custom-classname
    <div className="flex flex-col gap-spacing/gap/soft-6px">
      {sections.map(
        ({ id, shouldShow, render }) =>
          shouldShow && <div key={id}>{render(defaultOpenSection === id)}</div>,
      )}
    </div>
  );
};
